import { SHELL_SEARCH_ANCHOR_ATTR } from '@ordo/ui/mobile-floating-actions'

export interface MorphRect {
  top: number
  left: number
  width: number
  height: number
  borderRadius: number
  /** True when the rect is a host search trigger (the phone search circle), not the pill. */
  fromTrigger: boolean
}

type Box = Pick<DOMRect, 'top' | 'left' | 'width' | 'height'>

const PILL_RADIUS_PX = 12

function laidOut(box: Box | undefined): box is Box {
  return Boolean(box && box.width > 0 && box.height > 0)
}

/**
 * The rect the search morph springs from and back to. Normally the inline
 * pill; when the pill is not laid out (the unified shell hides the top bar
 * on phones), the first visible host search trigger marked with
 * `data-shell-search-anchor` (the bottom-right search circle). Without that
 * fallback the morph sprang from the hidden pill's empty rect at the
 * top-left corner. Null when nothing is on screen: the caller snaps open.
 */
export function pickMorphAnchor(pill: Box | undefined, triggers: Box[]): MorphRect | null {
  if (laidOut(pill)) {
    return {
      top: pill.top,
      left: pill.left,
      width: pill.width,
      height: pill.height,
      borderRadius: PILL_RADIUS_PX,
      fromTrigger: false,
    }
  }
  const trigger = triggers.find(laidOut)
  if (!trigger) {
    return null
  }
  return {
    top: trigger.top,
    left: trigger.left,
    width: trigger.width,
    height: trigger.height,
    borderRadius: Math.min(trigger.width, trigger.height) / 2,
    fromTrigger: true,
  }
}

/**
 * Where the close X sits while the search is open from a trigger: exactly on
 * the trigger, but lifted to stay above the on-screen keyboard (`--as-vh` /
 * `--as-vv-top` track the visible viewport).
 */
export function triggerCloseTop(rect: Pick<MorphRect, 'top' | 'height'>): string {
  const gapPx = 16
  return `min(${rect.top}px, calc(var(--as-vv-top, 0px) + var(--as-vh, 100dvh) - ${rect.height + gapPx}px))`
}

export function measureMorphAnchor(pill: HTMLElement | null): MorphRect | null {
  const triggers = Array.from(document.querySelectorAll(`[${SHELL_SEARCH_ANCHOR_ATTR}]`), (el) =>
    el.getBoundingClientRect(),
  )
  return pickMorphAnchor(pill?.getBoundingClientRect(), triggers)
}
